import { useEffect, useRef, useState } from 'react';

/**
 * Tiny scroll-reveal hook. Returns a ref to attach to the element and a boolean
 * flag that flips true the first time the element crosses the configured viewport
 * threshold. Disconnect-on-reveal so it never re-fires.
 */
export function useInView<T extends Element = HTMLDivElement>(
  options: IntersectionObserverInit = { threshold: 0.18, rootMargin: '0px 0px -10% 0px' }
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // SSR / older browsers: just reveal immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);

    observer.observe(node);
    return () => observer.disconnect();
    // options is referentially-new each render but threshold/rootMargin are stable
    // strings; spreading them would over-complicate the API. Mount-once is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, inView };
}
